import React, { useState, useEffect } from 'react';
import { 
  MagnifyingGlassIcon,
  DocumentTextIcon,
  BuildingOfficeIcon,
  UserGroupIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import { DocumentTextIcon as DocumentTextIconSolid } from '@heroicons/react/24/solid';
import { getCompaniesWithNotes } from '../api/notesApi';
import { CompanyWithNotes, Note } from '../types/note';
import NotesModal from '../components/notes/NotesModal';
import { toast } from 'react-toastify';
import { formatDate } from '../utils/dateUtils';

const NotesPage: React.FC = () => {
  const [companies, setCompanies] = useState<CompanyWithNotes[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [adherentFilter, setAdherentFilter] = useState<'all' | 'adherent' | 'non-adherent'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage] = useState(10);
  const [selectedCompany, setSelectedCompany] = useState<CompanyWithNotes | null>(null);
  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);

  // Load data when filters or page change
  useEffect(() => {
    loadCompanies();
  }, [currentPage, adherentFilter]);

  // Debounced search effect
  useEffect(() => {
    const searchTimeout = setTimeout(() => {
      if (currentPage === 1) {
        loadCompanies();
      } else {
        setCurrentPage(1);
      }
    }, 300);

    return () => clearTimeout(searchTimeout);
  }, [searchTerm]);

  const loadCompanies = async () => {
    setIsLoading(true);
    try {
      const params: {
        page: number;
        limit: number;
        search?: string;
        adherentFilter?: 'adherent' | 'non-adherent';
      } = {
        page: currentPage,
        limit: itemsPerPage
      };

      if (searchTerm.trim()) {
        params.search = searchTerm.trim();
      }

      if (adherentFilter !== 'all') {
        params.adherentFilter = adherentFilter;
      }

      const response = await getCompaniesWithNotes(params);
      setCompanies(response.data.companies);
      setCurrentPage(response.data.pagination.currentPage);
      setTotalPages(response.data.pagination.totalPages);
      setTotalItems(response.data.pagination.totalItems);
    } catch (error) {
      console.error('Error loading companies:', error);
      toast.error(error instanceof Error ? error.message : 'Erreur lors du chargement des entreprises');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    loadCompanies();
  };

  const handleFilterChange = (filter: 'all' | 'adherent' | 'non-adherent') => {
    setAdherentFilter(filter);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const openNotesModal = (company: CompanyWithNotes) => {
    setSelectedCompany(company);
    setIsNotesModalOpen(true);
  };

  const closeNotesModal = () => {
    setIsNotesModalOpen(false);
    setSelectedCompany(null);
    // Refresh the list to reflect any changes
    loadCompanies();
  };

  const getCompanyAddress = (company: CompanyWithNotes): string => {
    if (!company.etablissement) return '';
    
    const parts = [];
    if (company.etablissement.numeroVoieEtablissement) {
      parts.push(company.etablissement.numeroVoieEtablissement);
    }
    if (company.etablissement.typeVoieEtablissement) {
      parts.push(company.etablissement.typeVoieEtablissement);
    }
    if (company.etablissement.libelleVoieEtablissement) {
      parts.push(company.etablissement.libelleVoieEtablissement);
    }
    
    const street = parts.join(' ');
    const cityPart = [];
    if (company.etablissement.codePostalEtablissement) {
      cityPart.push(company.etablissement.codePostalEtablissement);
    }
    if (company.etablissement.libelleCommuneEtablissement) {
      cityPart.push(company.etablissement.libelleCommuneEtablissement);
    }
    
    const fullAddress = [street, cityPart.join(' ')].filter(Boolean).join(', ');
    return fullAddress;
  };

  const getLatestNote = (notes: Note[]): Note | null => {
    if (notes.length === 0) return null;
    return notes.reduce((latest, note) => 
      new Date(note.updatedAt) > new Date(latest.updatedAt) ? note : latest
    );
  };

  const truncateText = (text: string, maxLength: number): string => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pageNumbers = [];
    const maxVisiblePages = 5;
    
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return (
      <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
        <div className="flex flex-1 justify-between sm:hidden">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Précédent
          </button>
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Suivant
          </button>
        </div>
        <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-gray-700">
              Affichage de <span className="font-medium">{((currentPage - 1) * itemsPerPage) + 1}</span> à{' '}
              <span className="font-medium">
                {Math.min(currentPage * itemsPerPage, totalItems)}
              </span>{' '}
              sur <span className="font-medium">{totalItems}</span> résultats
            </p>
          </div>
          <div>
            <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeftIcon className="h-5 w-5" />
              </button>
              {pageNumbers.map((page) => (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                    page === currentPage
                      ? 'z-10 bg-blue-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600'
                      : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0'
                  }`}
                >
                  {page}
                </button>
              ))}
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRightIcon className="h-5 w-5" />
              </button>
            </nav>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <DocumentTextIconSolid className="h-7 w-7 mr-3 text-blue-600" />
              Notes
            </h1>
            <p className="mt-1 text-sm text-gray-600">
              Gérez toutes les notes sur les entreprises
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Search */}
          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="flex-1 relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher par SIREN, nom d'entreprise, dénomination ou contenu de note..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Rechercher
            </button>
            {searchTerm && (
              <button
                type="button"
                onClick={() => {
                  setSearchTerm('');
                  setCurrentPage(1);
                }}
                className="px-3 py-2 text-gray-500 hover:text-gray-700 focus:outline-none"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            )}
          </form>

          {/* Adherent Filter */}
          <div className="flex gap-2">
            <button
              onClick={() => handleFilterChange('all')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                adherentFilter === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Tous
            </button>
            <button
              onClick={() => handleFilterChange('adherent')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center ${
                adherentFilter === 'adherent'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <UserGroupIcon className="h-4 w-4 mr-1" />
              Adhérents
            </button>
            <button
              onClick={() => handleFilterChange('non-adherent')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center ${
                adherentFilter === 'non-adherent'
                  ? 'bg-orange-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <BuildingOfficeIcon className="h-4 w-4 mr-1" />
              Non-adhérents
            </button>
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-gray-600">Chargement...</span>
          </div>
        ) : companies.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <DocumentTextIcon className="h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune note trouvée</h3>
            <p className="text-gray-600 text-center">
              {searchTerm || adherentFilter !== 'all'
                ? 'Aucune entreprise ne correspond à vos critères de recherche.'
                : 'Aucune note n\'a été créée pour le moment.'}
            </p>
          </div>
        ) : (
          <>
            {/* Companies List */}
            <div className="divide-y divide-gray-200">
              {companies.map((company) => {
                const latestNote = getLatestNote(company.notes);
                const address = getCompanyAddress(company);
                
                return (
                  <div key={company.siren} className="p-6 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        {/* Company Header */}
                        <div className="flex items-center mb-2">
                          <h3 className="text-lg font-medium text-gray-900 truncate mr-3">
                            {company.companyName}
                          </h3>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            company.isAdherent
                              ? 'bg-green-100 text-green-800'
                              : 'bg-orange-100 text-orange-800'
                          }`}>
                            {company.isAdherent ? 'Adhérent' : 'Non-adhérent'}
                          </span>
                          <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {company.notes.length} note{company.notes.length > 1 ? 's' : ''}
                          </span>
                        </div>

                        {/* Company Details */}
                        <div className="text-sm text-gray-600 mb-3">
                          <p>SIREN: {company.siren}</p>
                          {company.uniteLegale?.activitePrincipaleUniteLegale && (
                            <p>Activité: {company.uniteLegale.activitePrincipaleUniteLegale}</p>
                          )}
                          {address && <p>Adresse: {address}</p>}
                        </div>

                        {/* Latest Note Preview */}
                        {latestNote && (
                          <div className="bg-gray-50 rounded-md p-3 border-l-4 border-blue-500">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs font-medium text-gray-700">
                                Dernière note
                              </span>
                              <span className="text-xs text-gray-500">
                                {formatDate(latestNote.updatedAt)}
                              </span>
                            </div>
                            <p className="text-sm text-gray-800">
                              {truncateText(latestNote.content, 150)}
                            </p>
                            {latestNote.creator && (
                              <p className="text-xs text-gray-500 mt-1">
                                Par {latestNote.creator.firstName} {latestNote.creator.lastName}
                              </p>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="ml-6 flex-shrink-0">
                        <button
                          onClick={() => openNotesModal(company)}
                          className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                        >
                          <EyeIcon className="h-4 w-4 mr-1" />
                          Voir les notes
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            {renderPagination()}
          </>
        )}
      </div>

      {/* Notes Modal */}
      {selectedCompany && (
        <NotesModal
          isOpen={isNotesModalOpen}
          onClose={closeNotesModal}
          siren={selectedCompany.siren}
          companyName={selectedCompany.companyName}
        />
      )}
    </div>
  );
};

export default NotesPage; 